/* eslint-disable max-lines */
import { Badge, LoadingSpinner, Text, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack, type Theme, ThemeProvider } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { convertToGeoLocation, type GeoLocation } from "@src/appV2/Location";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { SHIFT_DISCOVERY_LIST_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { WorkplaceModalsDataProvider } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addHours, differenceInMinutes, isAfter, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";

import { IconButton } from "../../components/IconButton";
import {
  type ExtraWorkedTimeRequestType,
  type ShiftType,
  usePaginatedWorkerShifts,
} from "../api/usePaginatedWorkerShifts";
import { PageContent } from "../components/PageContent";
import { SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH } from "../path";
import { ReturnForSignatureBottomSheet } from "../ShiftDetails/components/ReturnForSignatureBottomSheet";
import { SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY } from "../ShiftDetails/constants";
import { useMissedPunchRequestGeofenceCheck } from "../ShiftDetails/hooks/useMissedPunchRequestGeofenceCheck";
import { UploadTimesheetBottomSheet } from "../UnverifiedShifts/UploadTimesheet/UploadTimesheetBottomSheet";
import { ShiftHistoryCard } from "./Card";
import { ChatWithSupportBottomSheet } from "./ChatWithSupportBottomSheet";
import { ExtraWorkedTimeRequestBannerList } from "./components/ExtraWorkedTimeRequestBannerList";
import { ShiftHistoryEmptyState } from "./EmptyState";
import { ShiftHistoryFiltersBottomSheet } from "./Filters/BottomSheet/BottomSheet";
import { type FiltersFormValues, useFiltersForm } from "./Filters/BottomSheet/useFiltersForm";
import { ShiftHistoryLoadingPageState } from "./LoadingPageState";

// Minimum loader duration for recheck location action
const MIN_RECHECK_LOADER_DURATION_MS = 1000;

export function ShiftHistoryPage() {
  const worker = useDefinedWorker();
  const history = useHistory();

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_HISTORY_PAGE_VIEWED,
    { workerId: worker.userId },
    { enabled: isDefined(worker.userId) }
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const uploadTimesheetModalState = useModalState();
  const chatWithSupportModalState = useModalState();
  const returnForSignatureModalState = useModalState();

  const [selectedShift, setSelectedShift] = useState<ShiftType | undefined>(undefined);

  const { form, changedFiltersCount, resetFilters, cancelFilters, submitFilters, appliedFilters } =
    useFiltersForm();

  const [isRecheckingWithMinDuration, setIsRecheckingWithMinDuration] = useState(false);
  const recheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (recheckTimerRef.current) {
        clearTimeout(recheckTimerRef.current);
      }
    };
  }, []);

  const {
    data: shiftHistoryPages,
    isLoading: isInitialLoading,
    isSuccess,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchShifts,
  } = usePaginatedWorkerShifts(
    {
      workerId: worker.userId,
      timeline: "PAST_AND_IN_PROGRESS",
      sortBy: "schedule.startAt",
      sortOrder: "desc",
      pageSize: 5,
      fields: {
        shift: [
          "schedule.startAt",
          "schedule.endAt",
          "schedule.timeSlot",
          "schedule.durationWithoutBreaksInHours",
          "requirements.qualificationName",
          "pay.total",
          "clockInOut.start",
          "clockInOut.end",
          "lunchInOut.start",
          "lunchInOut.end",
          "verified",
          "isMissedPunchRequestEnabled",
          "timecard.files",
          "shiftEndGraceTimeHours",
        ],
        workplace: ["name", "timezone", "geoLocation"],
        extraWorkedTimeRequest: ["type", "status", "start", "end"],
      },
      include: ["workplace", "extra-worked-time-request"],
      filter: {
        deleted: false,
        ...(appliedFilters.shiftTimeSlots.length > 0 && {
          timeSlot: appliedFilters.shiftTimeSlots.join(","),
        }),
        ...(appliedFilters.licenses.length > 0 && {
          qualificationName: appliedFilters.licenses.join(","),
        }),
      },
    },
    {
      enabled: isDefined(worker.userId),
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
    }
  );

  const workplaceMap = useMemo(() => {
    const map = new Map<string, { name?: string; timezone?: string; geoLocation?: GeoLocation }>();
    shiftHistoryPages?.pages.forEach((page) => {
      page.included?.forEach((item) => {
        if (item.type === "workplace") {
          const coordinates = item.attributes?.geoLocation?.coordinates;
          const geoLocation = isDefined(coordinates)
            ? convertToGeoLocation(coordinates)
            : undefined;
          map.set(item.id, {
            name: item.attributes?.name,
            timezone: item.attributes?.timezone,
            geoLocation,
          });
        }
      });
    });
    return map;
  }, [shiftHistoryPages]);

  // Geofence check hook for MPR
  const { isCheckingGeofence, checkGeofence } = useMissedPunchRequestGeofenceCheck();

  // Navigate to submit timesheet page with or without signature
  const navigateToSubmitTimesheet = useCallback(
    (shiftId: string, skipWorkplaceSignature: boolean) => {
      const searchParams = new URLSearchParams({
        [SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY]: String(skipWorkplaceSignature),
      });
      history.push({
        pathname: generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, { shiftId }),
        search: searchParams.toString(),
      });
    },
    [history]
  );

  // Wrapper function to validate shift data and perform geofence check
  const validateAndCheckGeofence = useCallback(
    async (
      shift: ShiftType
    ): Promise<{ status: "inside" | "outside" | "unknown"; error?: string }> => {
      // Check workplace ID
      const workplaceId = shift.relationships?.workplace?.data?.id;
      if (!workplaceId) {
        return { status: "unknown", error: "No workplace ID" };
      }

      // Check workplace location
      const workplace = workplaceMap.get(workplaceId);
      const shiftWorkplaceLocation = workplace?.geoLocation;
      if (!shiftWorkplaceLocation) {
        return { status: "unknown", error: "No workplace location" };
      }

      // Check shift end time
      if (!isDefined(shift.attributes?.schedule?.endAt)) {
        return { status: "unknown", error: "No shift end time" };
      }

      // Perform geofence check
      const result = await checkGeofence({
        shiftId: shift.id,
        shiftEnd: shift.attributes.schedule.endAt,
        workerId: worker.userId,
        workplaceLocation: shiftWorkplaceLocation,
      });

      return { status: result.status };
    },
    [workplaceMap, checkGeofence, worker.userId]
  );

  // Handle the MPR request flow with geofence check
  const handleRequestShiftAdjustment = useCallback(
    async (shift: ShiftType) => {
      const result = await validateAndCheckGeofence(shift);

      // Inside geofence or unable to determine - navigate without signature
      if (result.status === "inside" || result.status === "unknown") {
        navigateToSubmitTimesheet(shift.id, false);
        setSelectedShift(undefined);
      } else {
        // Outside geofence - show return for signature modal
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
          shiftId: shift.id,
          workerId: worker.userId,
        });
        // Keep selectedShift set for modal interactions
        returnForSignatureModalState.openModal();
      }
    },
    [
      validateAndCheckGeofence,
      navigateToSubmitTimesheet,
      worker.userId,
      returnForSignatureModalState,
    ]
  );

  // Handle "I'm still at the workplace" - recheck geofence
  const handleRecheckLocation = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.MPR_RECHECK_LOCATION_CLICKED, {
      shiftId: selectedShift?.id,
      workerId: worker.userId,
    });

    if (!selectedShift) {
      return;
    }

    const startTime = Date.now();
    setIsRecheckingWithMinDuration(true);

    try {
      const result = await validateAndCheckGeofence(selectedShift);

      /**
       * We want to have a minimum loader duration so
       * user can see that the action was executed,
       * even when it lasted for a short time.
       */
      const timeElapsedInMs = Date.now() - startTime;
      if (timeElapsedInMs < MIN_RECHECK_LOADER_DURATION_MS) {
        await new Promise((resolve) => {
          recheckTimerRef.current = setTimeout(
            resolve,
            MIN_RECHECK_LOADER_DURATION_MS - timeElapsedInMs
          );
        });
      }

      // If now inside geofence or unable to determine - close modal and navigate
      if (result.status === "inside" || result.status === "unknown") {
        returnForSignatureModalState.closeModal();
        navigateToSubmitTimesheet(selectedShift.id, false);
        setSelectedShift(undefined);
      }
      // If still outside, keep the bottom sheet open (user can try again or choose other options)
    } finally {
      setIsRecheckingWithMinDuration(false);
    }
  }, [
    selectedShift,
    worker.userId,
    validateAndCheckGeofence,
    returnForSignatureModalState,
    navigateToSubmitTimesheet,
  ]);

  // Handle confirmation to continue without signature
  const handleConfirmWithoutSignature = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.MPR_CONFIRM_WITHOUT_SIGNATURE_CLICKED, {
      shiftId: selectedShift?.id,
      workerId: worker.userId,
    });
    if (selectedShift) {
      navigateToSubmitTimesheet(selectedShift.id, true);
    }
    setSelectedShift(undefined);
  }, [selectedShift, worker.userId, navigateToSubmitTimesheet]);

  const extraWorkedTimeRequestMap = useMemo(() => {
    const map = new Map<string, ExtraWorkedTimeRequestType["attributes"]>();
    shiftHistoryPages?.pages.forEach((page) => {
      page.included?.forEach((item) => {
        if (item.type === "extra-worked-time-request") {
          map.set(item.id, item.attributes);
        }
      });
    });
    return map;
  }, [shiftHistoryPages]);

  const shiftHistory = useMemo(() => {
    const allShifts = shiftHistoryPages?.pages.flatMap((page) => page.data) ?? [];

    // Filter out shifts that are still within the grace period and don't have a timecard or ewtr
    // These shifts are still visible in the bookings page
    return allShifts.filter((shift) => {
      const shiftEndAt = shift.attributes?.schedule?.endAt;
      const hasTimecard = (shift.attributes?.timecard?.files?.length ?? 0) > 0;

      // If shift has a timecard, always show it in history
      if (hasTimecard) {
        return true;
      }

      // If shift has a FULL_SHIFT MPR (Missed Punch Request), show it in history
      // This handles the Missed Punch Request "without signature" flow where shifts disappear from My Bookings
      // but don't have a timecard yet
      const hasMissedPunchRequest =
        shift.relationships?.extraWorkedTimeRequest?.data?.some(({ id }) => {
          const request = extraWorkedTimeRequestMap.get(id);
          return isDefined(request) && request.type === ExtraWorkedTimeType.FULL_SHIFT;
        }) ?? false;

      if (hasMissedPunchRequest) {
        return true;
      }

      // If no end time, include the shift
      if (!isDefined(shiftEndAt)) {
        return true;
      }

      const graceTimeHours = shift.attributes?.shiftEndGraceTimeHours ?? 6;

      // If shift is still within the grace period (end + graceHours > now), hide it
      // because it's still visible in the bookings page
      const shiftEndWithGrace = addHours(parseISO(shiftEndAt), graceTimeHours);
      return isAfter(new Date(), shiftEndWithGrace);
    });
  }, [shiftHistoryPages, extraWorkedTimeRequestMap]);

  const filtersModalState = useModalState();

  return (
    <WorkplaceModalsDataProvider
      baseNavigationPath={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`}
    >
      <ThemeProvider theme={getTheme()}>
        <FormProvider<FiltersFormValues> {...form}>
          <PageWrapper variant="tertiary">
            <StickyScrollPageHeader
              title="Shift history"
              scrollContainerRef={scrollRef}
              headerVariant="tertiary"
              rightAction={
                <Badge
                  count={changedFiltersCount}
                  overlap="circular"
                  color="primary"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <IconButton
                    isSelected={changedFiltersCount > 0}
                    variant="outlined"
                    aria-label={
                      changedFiltersCount > 0
                        ? `Open Filters Button (${changedFiltersCount} applied)`
                        : "Open Filters Button"
                    }
                    size="medium"
                    iconType="slider-settings"
                    onClick={() => {
                      filtersModalState.openModal();
                    }}
                  />
                </Badge>
              }
            />
            <PageContent
              ref={scrollRef}
              sx={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <PullToRefresh onRefresh={refetchShifts} />
              <ExtraWorkedTimeRequestBannerList />
              {isInitialLoading && <ShiftHistoryLoadingPageState />}
              {isError && <Alert severity="error">Error loading shift history.</Alert>}
              {isSuccess && shiftHistory.length > 0 && (
                <Virtuoso
                  data={shiftHistory}
                  customScrollParent={scrollRef.current ?? undefined}
                  width="100%"
                  increaseViewportBy={2000}
                  topItemCount={0}
                  overscan={5}
                  endReached={() => {
                    if (hasNextPage) {
                      void fetchNextPage();
                    }
                  }}
                  components={{
                    Footer: () => {
                      if (isFetchingNextPage) {
                        return (
                          <Stack px={5} minHeight="76px">
                            <Stack alignItems="center" py={8}>
                              <LoadingSpinner size="medium" variant="primary" />
                              <Text
                                mt={4}
                                pb={10}
                                color={(theme: Theme) => theme.palette.text.secondary}
                              >
                                Loading more shifts...
                              </Text>
                            </Stack>
                          </Stack>
                        );
                      }

                      // Minimal footer with just bottom spacing when not loading
                      return <Stack pb={6} />;
                    },
                  }}
                  itemContent={(_, shift) => {
                    const workplaceId = shift.relationships?.workplace?.data?.id;
                    if (!workplaceId) {
                      return null;
                    }

                    const workplaceInfo = workplaceMap.get(workplaceId);

                    const hasPendingExtraWorkedTimeRequest =
                      shift.relationships?.extraWorkedTimeRequest?.data?.some(({ id }) => {
                        const request = extraWorkedTimeRequestMap.get(id);
                        return request?.status === ExtraWorkedTimeRequestStatus.PENDING;
                      }) ?? false;

                    const approvedBreakTimeInMinutes =
                      shift.relationships?.extraWorkedTimeRequest?.data?.reduce(
                        (accumulator, { id }) => {
                          const request = extraWorkedTimeRequestMap.get(id);
                          if (!isDefined(request)) {
                            return accumulator;
                          }

                          if (
                            request.type !== ExtraWorkedTimeType.BREAK ||
                            request.status !== ExtraWorkedTimeRequestStatus.APPROVED
                          ) {
                            return accumulator;
                          }

                          if (isDefined(request.end) && isDefined(request.start)) {
                            return (
                              accumulator +
                              differenceInMinutes(parseISO(request.end), parseISO(request.start))
                            );
                          }

                          return accumulator;
                        },
                        0
                      ) ?? 0;

                    const totalShiftTimeInHours =
                      (shift.attributes?.schedule?.durationWithoutBreaksInHours ?? 0) +
                      approvedBreakTimeInMinutes / 60;
                    return (
                      <ShiftHistoryCard
                        key={shift.id}
                        shift={shift}
                        workplaceInfo={workplaceInfo}
                        totalShiftTimeInHours={totalShiftTimeInHours}
                        isMissedPunchRequestEnabled={
                          shift.attributes?.isMissedPunchRequestEnabled ?? false
                        }
                        hasPendingExtraWorkedTimeRequest={hasPendingExtraWorkedTimeRequest}
                        isTimesheetUploaded={(shift.attributes?.timecard?.files?.length ?? 0) > 0}
                        shouldDisableButton={isCheckingGeofence}
                        isRequestingShiftAdjustment={
                          selectedShift?.id === shift.id && isCheckingGeofence
                        }
                        onUploadTimesheetClick={() => {
                          setSelectedShift(shift);
                          logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_TAPPED, {
                            shiftId: shift.id,
                          });
                          uploadTimesheetModalState.openModal();
                        }}
                        onChatWithSupportClick={() => {
                          setSelectedShift(shift);
                          chatWithSupportModalState.openModal();
                        }}
                        onRequestShiftAdjustmentClick={() => {
                          setSelectedShift(shift);
                          void handleRequestShiftAdjustment(shift);
                        }}
                      />
                    );
                  }}
                />
              )}
              {isSuccess && shiftHistory.length === 0 && (
                <ShiftHistoryEmptyState filtersAreApplied={changedFiltersCount > 0} />
              )}
            </PageContent>
            <ShiftHistoryFiltersBottomSheet
              modalState={filtersModalState}
              changedFiltersCount={changedFiltersCount}
              resetFilters={resetFilters}
              cancelFilters={cancelFilters}
              onSubmit={submitFilters}
            />
            <UploadTimesheetBottomSheet
              showDescriptionText
              modalState={uploadTimesheetModalState}
              shiftId={selectedShift?.id}
              isTimesheetUploaded={(selectedShift?.attributes?.timecard?.files?.length ?? 0) > 0}
              onUploadStarted={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_STARTED, {
                  shiftId: selectedShift?.id,
                  workerId: worker.userId,
                });
              }}
              onSuccess={async ({ uploadDurationInMs, updateTimecardDurationInMs }) => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_UPLOADED, {
                  shiftId: selectedShift?.id,
                  workerId: worker.userId,
                  isReupload: (selectedShift?.attributes?.timecard?.files?.length ?? 0) > 0,
                  uploadDurationInMs,
                  updateTimecardDurationInMs,
                });
                setSelectedShift(undefined);
                await refetchShifts();
              }}
              onError={(error) => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_UPLOAD_ERROR, {
                  shiftId: selectedShift?.id,
                  workerId: worker.userId,
                  error: error?.message ?? "Unknown error",
                });
                setSelectedShift(undefined);
              }}
            />
            {isDefined(selectedShift) && (
              <ChatWithSupportBottomSheet
                modalState={chatWithSupportModalState}
                shiftId={selectedShift.id}
              />
            )}
            {/* MPR Geofence Check Bottom Sheet */}
            <ReturnForSignatureBottomSheet
              modalState={returnForSignatureModalState}
              isCheckingLocation={isRecheckingWithMinDuration}
              isShiftVerified={selectedShift?.attributes?.verified ?? false}
              onRecheckLocation={handleRecheckLocation}
              onCancel={() => {
                returnForSignatureModalState.closeModal();
                setSelectedShift(undefined);
              }}
              onConfirm={handleConfirmWithoutSignature}
            />
          </PageWrapper>
        </FormProvider>
      </ThemeProvider>
    </WorkplaceModalsDataProvider>
  );
}
/* eslint-enable max-lines */
